<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useEventListener } from '@vueuse/core';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Accordion from 'dashboard/components-next/Accordion/Accordion.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { guideKeyForRoute, PAGE_GUIDE_HIDDEN_KEY } from './pageGuides';

// A fixed, icon-only launcher on the end edge of every dashboard page, at
// mid-height so it never meets the bottom-corner widgets (copilot launcher,
// call widget, mobile sidebar launcher). It opens a side panel with the guide
// of the current page: what the page is for, what each part does, how to do
// each task, and tips. Each user can hide it from Profile settings → Interface.
const route = useRoute();
const { t, tm, rt, locale, mergeLocaleMessage } = useI18n();
const { uiSettings } = useUISettings();

const isOpen = ref(false);
const isHiddenByUser = computed(
  () => uiSettings.value?.[PAGE_GUIDE_HIDDEN_KEY] === true
);

// The guides themselves (~0.5 MB of text per language) are NOT part of the
// language chunk: they are fetched the first time the panel opens, then merged
// into the running locale. `pagesVersion` bumps after every merge so the
// computeds below re-read the messages.
const PAGES_LOADERS = {
  ar: () => import('dashboard/i18n/locale/ar/teletelPageGuidePages.json'),
  en: () => import('dashboard/i18n/locale/en/teletelPageGuidePages.json'),
};
const pagesVersion = ref(0);
const isLoadingPages = ref(false);
const loadError = ref(false);

const pagesLoadedForLocale = () => {
  const pages = tm('TELETEL_PAGE_GUIDE.PAGES');
  return pages && typeof pages === 'object' && Object.keys(pages).length > 0;
};
const ensurePages = async () => {
  if (pagesLoadedForLocale()) return;
  const loader = PAGES_LOADERS[locale.value];
  if (!loader) return;
  isLoadingPages.value = true;
  loadError.value = false;
  try {
    const module = await loader();
    mergeLocaleMessage(locale.value, {
      TELETEL_PAGE_GUIDE: { PAGES: module.default.PAGES },
    });
    pagesVersion.value += 1;
  } catch {
    loadError.value = true;
  } finally {
    isLoadingPages.value = false;
  }
};

const guideKey = computed(() => guideKeyForRoute(route.name));
const K = computed(() => `TELETEL_PAGE_GUIDE.PAGES.${guideKey.value}`);
const hasGuide = computed(() => Boolean(guideKey.value));
const pagesReady = computed(
  () =>
    pagesVersion.value >= 0 && !isLoadingPages.value && pagesLoadedForLocale()
);

// vue-i18n returns raw messages from tm(); rt() resolves each string.
const list = key => {
  if (!pagesReady.value) return [];
  const items = tm(`${K.value}.${key}`);
  return Array.isArray(items) ? items.map(item => rt(item)) : [];
};
const parts = computed(() => {
  if (!pagesReady.value) return [];
  const items = tm(`${K.value}.PARTS`);
  if (!Array.isArray(items)) return [];
  return items.map(item => ({
    title: rt(item.TITLE),
    text: rt(item.TEXT),
  }));
});
const tasks = computed(() => {
  if (!pagesReady.value) return [];
  const items = tm(`${K.value}.TASKS`);
  if (!Array.isArray(items)) return [];
  return items.map(item => ({
    title: rt(item.TITLE),
    steps: Array.isArray(item.STEPS) ? item.STEPS.map(step => rt(step)) : [],
    note: item.NOTE ? rt(item.NOTE) : '',
  }));
});

const pageTitle = computed(() => {
  if (!hasGuide.value) return t('TELETEL_PAGE_GUIDE.NO_GUIDE.TITLE');
  if (!pagesReady.value) return t('TELETEL_PAGE_GUIDE.LAUNCHER.LABEL');
  return t(`${K.value}.TITLE`);
});

const close = () => {
  isOpen.value = false;
};
const toggle = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) ensurePages();
};

// A language switch replaces the locale's messages; fetch the guides of the
// new language on the next open.
watch(locale, () => {
  if (isOpen.value) ensurePages();
});

useEventListener(document, 'keydown', event => {
  if (event.key === 'Escape' && isOpen.value) close();
});

// Moving to another page closes the panel: its content belongs to the page
// it was opened on.
watch(() => route.name, close);
</script>

<template>
  <TeleportWithDirection to="body">
    <button
      v-if="!isHiddenByUser"
      type="button"
      class="fixed top-1/2 -translate-y-1/2 end-0 z-50 grid place-content-center size-10 rounded-s-full bg-n-brand text-white shadow-lg ring-1 ring-white/20 transition-all duration-200 ease-out hover:w-12 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-n-brand/60"
      :class="{ 'translate-x-full rtl:-translate-x-full': isOpen }"
      :title="t('TELETEL_PAGE_GUIDE.LAUNCHER.TOOLTIP')"
      :aria-label="t('TELETEL_PAGE_GUIDE.LAUNCHER.LABEL')"
      :aria-expanded="isOpen"
      data-testid="teletel-page-guide-launcher"
      @click="toggle"
    >
      <span class="i-lucide-book-open size-5" />
    </button>

    <Transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-150"
      leave-to-class="opacity-0"
    >
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[60] bg-n-alpha-black1 backdrop-blur-[2px]"
        @click="close"
      />
    </Transition>

    <Transition
      enter-active-class="transition-transform duration-200 ease-out"
      enter-from-class="translate-x-full rtl:-translate-x-full"
      leave-active-class="transition-transform duration-150 ease-in"
      leave-to-class="translate-x-full rtl:-translate-x-full"
    >
      <aside
        v-if="isOpen"
        class="fixed top-0 bottom-0 end-0 z-[70] flex w-full max-w-md flex-col bg-n-solid-1 text-n-slate-12 shadow-2xl border-s border-n-weak"
        role="dialog"
        aria-modal="true"
        :aria-label="t('TELETEL_PAGE_GUIDE.LAUNCHER.LABEL')"
        data-testid="teletel-page-guide-panel"
      >
        <header
          class="flex items-start justify-between gap-3 border-b border-n-weak px-5 py-4"
        >
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="grid size-9 shrink-0 place-content-center rounded-full bg-n-brand/10 text-n-brand"
            >
              <span class="i-lucide-book-open size-5" />
            </span>
            <div class="min-w-0">
              <p class="mb-0 text-xs text-n-slate-11">
                {{ t('TELETEL_PAGE_GUIDE.LAUNCHER.LABEL') }}
              </p>
              <h2 class="mb-0 text-base font-semibold leading-6 truncate">
                {{ pageTitle }}
              </h2>
            </div>
          </div>
          <Button
            icon="i-lucide-x"
            ghost
            slate
            sm
            :aria-label="t('TELETEL_PAGE_GUIDE.CLOSE')"
            @click="close"
          />
        </header>

        <div class="flex-1 overflow-y-auto px-5 py-4">
          <div
            v-if="hasGuide && isLoadingPages"
            class="flex flex-col items-center gap-3 py-10 text-center text-sm text-n-slate-11"
          >
            <span
              class="i-lucide-loader-circle size-8 animate-spin text-n-slate-9"
            />
            <p class="mb-0">{{ t('TELETEL_PAGE_GUIDE.LOADING') }}</p>
          </div>
          <div
            v-else-if="hasGuide && loadError"
            class="flex flex-col items-center gap-3 py-10 text-center text-sm text-n-slate-11"
          >
            <span class="i-lucide-circle-alert size-8 text-n-ruby-9" />
            <p class="mb-0">{{ t('TELETEL_PAGE_GUIDE.LOAD_ERROR') }}</p>
          </div>
          <template v-else-if="hasGuide && pagesReady">
            <div class="flex flex-col gap-6 text-sm leading-6">
              <section>
                <h3 class="mb-1 text-sm font-semibold">
                  {{ t('TELETEL_PAGE_GUIDE.SECTIONS.WHAT') }}
                </h3>
                <p class="mb-0 text-n-slate-11">{{ t(`${K}.WHAT`) }}</p>
              </section>

              <section>
                <h3 class="mb-1 text-sm font-semibold">
                  {{ t('TELETEL_PAGE_GUIDE.SECTIONS.WHY') }}
                </h3>
                <ul
                  class="mb-0 list-disc ps-5 flex flex-col gap-1 text-n-slate-11"
                >
                  <li v-for="(item, i) in list('WHY')" :key="i">{{ item }}</li>
                </ul>
              </section>

              <section>
                <h3 class="mb-2 text-sm font-semibold">
                  {{ t('TELETEL_PAGE_GUIDE.SECTIONS.PARTS') }}
                </h3>
                <dl class="mb-0 flex flex-col gap-3">
                  <div
                    v-for="(part, i) in parts"
                    :key="i"
                    class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
                  >
                    <dt class="font-medium">{{ part.title }}</dt>
                    <dd class="mb-0 text-n-slate-11">{{ part.text }}</dd>
                  </div>
                </dl>
              </section>

              <section>
                <h3 class="mb-2 text-sm font-semibold">
                  {{ t('TELETEL_PAGE_GUIDE.SECTIONS.TASKS') }}
                </h3>
                <div class="flex flex-col gap-2">
                  <Accordion
                    v-for="(task, i) in tasks"
                    :key="i"
                    :title="task.title"
                  >
                    <ol
                      class="mb-0 list-decimal ps-5 flex flex-col gap-1 text-n-slate-11"
                    >
                      <li v-for="(step, j) in task.steps" :key="j">
                        {{ step }}
                      </li>
                    </ol>
                    <p
                      v-if="task.note"
                      class="mb-0 mt-2 rounded-md bg-n-amber-3 px-2 py-1 text-xs text-n-amber-11"
                    >
                      {{ task.note }}
                    </p>
                  </Accordion>
                </div>
              </section>

              <section>
                <h3 class="mb-1 text-sm font-semibold">
                  {{ t('TELETEL_PAGE_GUIDE.SECTIONS.TIPS') }}
                </h3>
                <ul
                  class="mb-0 list-disc ps-5 flex flex-col gap-1 text-n-slate-11"
                >
                  <li v-for="(item, i) in list('TIPS')" :key="i">{{ item }}</li>
                </ul>
              </section>

              <section>
                <h3 class="mb-1 text-sm font-semibold">
                  {{ t('TELETEL_PAGE_GUIDE.SECTIONS.WHO') }}
                </h3>
                <p class="mb-0 text-n-slate-11">{{ t(`${K}.WHO`) }}</p>
              </section>
            </div>
          </template>

          <div
            v-else-if="!hasGuide"
            class="flex flex-col items-center gap-3 py-10 text-center text-sm text-n-slate-11"
          >
            <span class="i-lucide-book-dashed size-10 text-n-slate-9" />
            <p class="mb-0">{{ t('TELETEL_PAGE_GUIDE.NO_GUIDE.TEXT') }}</p>
          </div>
        </div>

        <footer
          class="border-t border-n-weak px-5 py-3 text-xs text-n-slate-11"
        >
          {{ t('TELETEL_PAGE_GUIDE.FOOTER') }}
        </footer>
      </aside>
    </Transition>
  </TeleportWithDirection>
</template>
